import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, FileText, Loader2, PenLine, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useProfile } from '@/shared/auth/SessionProvider';
import {
  emitirCertificado,
  prepareDespacho,
  type MantenimientoRow,
} from '../api';
import {
  certificadoGarantiaFileName,
  generateCertificadoGarantiaPDF,
} from '../pdf/certificadoGarantia';

/**
 * Certificado de Garantía — client-side PDF generation → blob upload
 * → status flip. Mirrors PowerApps btn_CertificadoGarantia but with
 * in-app preview before commit (the PowerApps version jumped straight
 * to upload).
 */
export function CertificadoGarantiaDialog({
  summary,
  onClose,
}: {
  summary: MantenimientoRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const prepareQ = useQuery({
    queryKey: ['mantenimiento', 'prepare-despacho', summary.id],
    queryFn: () => prepareDespacho(summary.id),
    staleTime: 10_000,
  });

  const profile = useProfile();
  const defaultResponsable = profile?.displayName || profile?.usuario_app || '';
  const [responsable, setResponsable] = useState<string>(defaultResponsable);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  // Toggles a separate, full-bleed preview dialog. Keeps this main
  // dialog uncluttered — the iframe lives in its own modal so the
  // form stays compact regardless of viewport size.
  const [previewOpen, setPreviewOpen] = useState(false);

  // Clean up blob URLs when the component unmounts or a new preview is generated.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleGeneratePreview = async () => {
    if (!prepareQ.data) return;
    setGenerating(true);
    try {
      const blob = await generateCertificadoGarantiaPDF({
        venta: summary,
        detalles: prepareQ.data.detalles,
        direccionDespacho:
          summary.fields.DireccionCompletaDespacho_RO ||
          prepareQ.data.suggestedDireccion ||
          '',
        recibe:
          summary.fields.RecibeDespacho_RO ||
          prepareQ.data.suggestedRecibe ||
          '',
        responsable: responsable.trim() || '—',
        telefono: summary.telefono,
      });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(blob);
      setPdfBlob(blob);
      setPreviewUrl(url);
    } catch (err) {
      toast.error('No se pudo generar el PDF', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async () => {
    if (!pdfBlob) return;
    setSubmitting(true);
    try {
      const result = await emitirCertificado(
        summary.id,
        pdfBlob,
        certificadoGarantiaFileName(summary.id),
      );
      if (result.outcome === 'issued') {
        toast.success('Certificado emitido', {
          description: `Se adjuntó ${result.fileName} a la venta.`,
        });
        qc.invalidateQueries({ queryKey: ['mantenimiento'] });
        onClose();
      } else {
        toast.info('No aplicable', { description: result.message });
      }
    } catch (err) {
      toast.error('No se pudo emitir el certificado', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const busy = generating || submitting;

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent
        className="max-w-3xl"
        processing={submitting && 'Adjuntando certificado…'}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4" aria-hidden />
            Certificado de Garantía · {summary.fields.Cliente_RO || 'Cliente'}
          </DialogTitle>
          <DialogDescription>
            Se genera el certificado localmente, lo previsualizas y luego se
            adjunta a la venta.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {prepareQ.isPending ? (
            <Skeleton className="h-24 w-full" />
          ) : prepareQ.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              No se pudieron cargar los datos del certificado.
            </div>
          ) : (
            <>
              <section className="rounded-lg border bg-muted/20 p-3 text-sm">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Datos del certificado
                </h3>
                <dl className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <DataItem label="Cliente" value={summary.fields.Cliente_RO} />
                  <DataItem
                    label="Fecha pago"
                    value={summary.fields.FechaPago_RO}
                  />
                  <DataItem
                    label="Recibe"
                    value={
                      summary.fields.RecibeDespacho_RO ||
                      prepareQ.data.suggestedRecibe
                    }
                  />
                  <DataItem
                    label="Dirección"
                    value={
                      summary.fields.DireccionCompletaDespacho_RO ||
                      prepareQ.data.suggestedDireccion
                    }
                  />
                </dl>
              </section>

              <section className="space-y-2 rounded-lg border bg-muted/20 p-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Artículos ({prepareQ.data.detalles.length})
                </h3>
                {prepareQ.data.detalles.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    No hay artículos activos en la venta.
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {prepareQ.data.detalles.map((d) => (
                      <li
                        key={d.id}
                        className="flex items-center justify-between rounded-md bg-background px-2.5 py-1.5 text-sm"
                      >
                        <div>
                          <span className="font-medium">
                            {d.modelo || d.articulo || '—'}
                          </span>
                          {d.modelo &&
                            d.articulo &&
                            d.modelo !== d.articulo && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                {d.articulo}
                              </span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{d.cantidad} un.</span>
                          {d.currentSerie && (
                            <span>{d.currentSerie}</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto_auto]">
                <div className="space-y-1">
                  <Label htmlFor="cert-responsable" className="text-sm">
                    <PenLine className="mr-1 inline h-3.5 w-3.5" aria-hidden />
                    Responsable que firma
                  </Label>
                  <Input
                    id="cert-responsable"
                    value={responsable}
                    onChange={(e) => setResponsable(e.target.value)}
                    placeholder="Nombre y apellido"
                    maxLength={80}
                    disabled={busy}
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={handleGeneratePreview}
                  disabled={busy}
                  className="md:mt-6 gap-2"
                  title="Generar PDF y previsualizar"
                >
                  {generating ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden />
                  )}
                  {previewUrl ? 'Regenerar' : 'Generar PDF'}
                </Button>
                {previewUrl && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPreviewOpen(true)}
                    className="md:mt-6 gap-2"
                    title="Previsualizar certificado"
                  >
                    <Eye className="h-4 w-4" aria-hidden />
                    Previsualizar
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!pdfBlob || busy}
            className="gap-2"
          >
            <Send className="h-4 w-4" aria-hidden />
            Adjuntar a la venta
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Standalone preview dialog. Renders the PDF in a wide modal
          so the iframe gets real estate without crowding the main
          form. Available only after the user has actually generated
          a preview (`previewUrl` non-null). */}
      {previewUrl && (
        <Dialog
          open={previewOpen}
          onOpenChange={(o) => !o && setPreviewOpen(false)}
        >
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-4 w-4" aria-hidden />
                Vista previa · Certificado de Garantía
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <div className="overflow-hidden rounded-md border">
                <iframe
                  title="Vista previa certificado"
                  src={previewUrl}
                  className="h-[70vh] w-full bg-muted"
                />
              </div>
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewOpen(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

function DataItem({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="font-medium text-foreground">{value?.trim() || '—'}</dd>
    </div>
  );
}
